/**
 * Relay utility functions
 */

/**
 * Strip HTML tags for plain text display.
 * Used to show email body as plain text by default.
 */
export function stripHtml(html: string): string {
  return html.replaceAll(/<[^>]*>/g, '').trim();
}

/**
 * Decode HTML entities (`&nbsp;`, `&amp;`, `&#39;`, …) to their literal
 * characters. Email bodies frequently carry entities that would otherwise
 * render verbatim in our plain-text `<p>`. Uses a detached `<textarea>` so
 * character references are decoded without parsing or executing any markup
 * (textarea content is RCDATA — tags are preserved as literal text).
 */
export function decodeHtmlEntities(text: string): string {
  if (!text || typeof document === 'undefined') return text;
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

/**
 * Pick the body to render by default for a message.
 *
 * Preference, in order:
 *   1. Trimmed view — `bodyCleaned[:tailStart] + bodyCleaned[tailEnd:]` when
 *      `tailStatus === 'llm_cleaned'` AND offsets are present + in range.
 *   2. Cleaned view — `bodyCleaned` when present (cleaner ran but tail did not
 *      cut, e.g. `no_tail_detected`).
 *   3. Raw fallback — `rawBody` stripped of HTML.
 *
 * TODO: the spec also calls for a SHA-256 drift guard
 * (sha256(bodyCleaned) === tailBodyHash); when the hashes disagree, fall back
 * to (2). Skipped here because hashing per-render is async and this is the
 * minimal v1. We pass `tailBodyHash` through so a future PR can wire it up.
 */
export function getDisplayBody(message: {
  rawBody: string;
  bodyCleaned: string | null;
  tailStatus: string;
  tailStart: number | null;
  tailEnd: number | null;
}): {body: string; isTrimmed: boolean; isCleaned: boolean} {
  const {rawBody, bodyCleaned, tailStatus, tailStart, tailEnd} = message;

  if (
    bodyCleaned &&
    tailStatus === 'llm_cleaned' &&
    tailStart !== null &&
    tailEnd !== null &&
    tailStart >= 0 &&
    tailEnd <= bodyCleaned.length &&
    tailStart <= tailEnd
  ) {
    return {
      body: decodeHtmlEntities(
        bodyCleaned.slice(0, tailStart) + bodyCleaned.slice(tailEnd)
      ),
      isTrimmed: true,
      isCleaned: true,
    };
  }

  if (bodyCleaned) {
    return {
      body: decodeHtmlEntities(bodyCleaned),
      isTrimmed: false,
      isCleaned: true,
    };
  }

  return {
    body: decodeHtmlEntities(stripHtml(rawBody)),
    isTrimmed: false,
    isCleaned: false,
  };
}

/**
 * The placeholder relay substitutes for an empty forward cover
 * (`pipeline/step_2_parse/handler.py`). Mirrors `PLACEHOLDER_BODY` in
 * `backend/src/matic/relay/domain/services/degenerate_body.py`.
 */
export const PLACEHOLDER_BODY = '[Forwarded without additional content]';

// Same zero-width set the backend strips before comparing bodies
// (BOM, ZWSP, word-joiner).
const ZERO_WIDTH_RE = /[\uFEFF\u200B\u2060]/g;

/**
 * True when a display body carries no sender-authored content.
 *
 * This is a *render-time* check, deliberately not a backend fix. A message
 * whose cover segment is nothing but an Outlook auto-signature is not empty
 * when relay substitutes its placeholder (handler.py substitutes before the
 * LLM tail extractor runs), so it keeps its ~350-char signature body; the tail
 * extractor then correctly cuts the whole thing, and the message arrives with
 * `tailStatus='llm_cleaned'` and a trimmed body of `''`. Rewriting the body
 * upstream after tail extraction would clear the tail offsets and break
 * `is_contentless_forward()` (`relay/domain/services/message_ordering.py`),
 * regressing `last_content_activity_at`. "The tail covers the entire body" is
 * how the system says "this message adds nothing new" — so the two contentless
 * shapes (literal placeholder, trimmed-to-empty) are collapsed here, in the UI.
 *
 * Normalization mirrors `_normalize` in `degenerate_body.py` so both sides
 * agree on what "contentless" means.
 */
export function isContentlessBody(body: string): boolean {
  const normalized = body
    .replaceAll(ZERO_WIDTH_RE, '')
    .replaceAll(/\s+/g, ' ')
    .trim();
  return normalized === '' || normalized === PLACEHOLDER_BODY;
}

/**
 * Extract display name from an email sender string.
 * "Alice Smith <alice@example.com>" → "Alice Smith"
 * "alice@example.com" → "alice@example.com"
 */
export function formatSender(sender: string): string {
  const match = /^(.+?)\s*<[^>]+>$/.exec(sender);
  return match ? match[1].trim() : sender;
}

/**
 * Format a timestamp as a human-readable absolute date.
 * "Feb 27, 2026 at 3:45 PM"
 */
export function formatFullDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Compact absolute timestamp without year or timezone, e.g. "6/10, 7:28 AM".
 * For dense timeline rows; the full year + tz lives in the hover tooltip
 * (`formatAbsoluteTimestamp`).
 */
export function formatCompactTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Absolute timestamp with year and timezone, e.g. "Feb 27, 2026, 3:45 PM PST". */
export function formatAbsoluteTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Format a due date string (YYYY-MM-DD) for display, with proximity context.
 *
 * Returns a label and a Tailwind color class:
 *   past     → "Feb 28"  text-error-500
 *   today    → "Today"   text-warning-600
 *   ≤3 days  → "Feb 28"  text-warning-600
 *   this yr  → "Feb 28"  text-neutral-400
 *   other yr → "Feb 28, 2026"  text-neutral-400
 *
 * Parses as a LOCAL date to avoid UTC-midnight timezone drift.
 */
export function formatDueDate(dateStr: string): {
  label: string;
  colorClass: string;
} {
  const [year, month, day] = dateStr.split('-').map(Number);
  const due = new Date(year, month - 1, day);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round(
    (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  const sameYear = due.getFullYear() === today.getFullYear();
  const label =
    diffDays === 0
      ? 'Today'
      : due.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          ...(sameYear ? {} : {year: 'numeric'}),
        });

  const colorClass =
    diffDays < 0
      ? 'text-error-500'
      : diffDays <= 3
        ? 'text-warning-600'
        : 'text-neutral-400';

  return {label, colorClass};
}

/**
 * Format a timestamp as a day-bucket label for Relay timelines.
 * Mirrors the previous date-fns-backed behavior without adding a runtime
 * dependency to the frontend bundle.
 */
export function formatTimelineDayLabel(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round(
    (today.getTime() - dateDay.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return date.toLocaleDateString(undefined, {weekday: 'long'});
  }

  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : {year: 'numeric'}),
  });
}

/**
 * Format a timestamp as a relative age string.
 * "2h ago", "3d ago", "5m ago", etc.
 */
export function formatAge(createdAt: string): string {
  const now = Date.now();
  const then = new Date(createdAt).getTime();
  const diffMs = now - then;

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// Short-term workaround for inline-image attribution gap: the Relay lambda
// writes [inline image: filename] placeholders into body_cleaned when it
// replaces CID references, but sha256 cover-first dedup means the
// EmailAttachment row ends up on the cover (forwarded) message rather than
// the originating message whose body contains the placeholder.
export function extractInlineImageFilenames(body: string): string[] {
  return [...body.matchAll(/\[inline image: ([^\]]+)]/g)].map((m) => m[1]);
}

/**
 * Second pass over `attachmentsByMessageId`: for each message whose
 * `bodyCleaned` contains `[inline image: filename]` placeholders, surface the
 * matching ingested attachment chip on that message even when the
 * `EmailAttachment` row was deduped onto a different message (the cover /
 * forwarded copy) by the sha256 cover-first dedup in the Relay lambda.
 *
 * When an attachment is surfaced onto the message that genuinely references it,
 * it is also *removed* from its dedup-host bucket if that host doesn't itself
 * reference the file in its body. This turns the copy into a move for pure
 * forward-cover duplicates, so a "forwarded without additional content" cover
 * doesn't render a strip of chips that were all retagged onto earlier messages.
 * A message that genuinely embeds the image (its own body has the placeholder)
 * keeps its chip.
 *
 * Mutates and returns `map` in place so callers can chain it into an existing
 * `useMemo` build loop.
 */
function removeAttachmentFromBucket<A extends {id: string}>(
  map: Map<string, A[]>,
  messageId: string,
  attachmentId: string
): void {
  const bucket = map.get(messageId);
  if (!bucket) return;
  const filtered = bucket.filter((a) => a.id !== attachmentId);
  if (filtered.length > 0) {
    map.set(messageId, filtered);
  } else {
    map.delete(messageId);
  }
}

export function applyCrossMessageInlineImages<
  A extends {
    id: string;
    emailMessageId: string;
    filename: string;
    status: string;
  },
  M extends {id: string; bodyCleaned?: string | null},
>(
  map: Map<string, A[]>,
  messages: M[] | undefined,
  attachments: A[]
): Map<string, A[]> {
  const ingestedByFilename = new Map(
    attachments
      .filter((a) => a.status === 'ingested')
      .map((a) => [a.filename, a])
  );
  // Inline-image filenames each message references in its own body. Used both to
  // surface chips on referencing messages and to decide whether a dedup-host
  // still warrants its chip.
  const referencedByMessage = new Map<string, Set<string>>();
  for (const msg of messages ?? []) {
    if (!msg.bodyCleaned) continue;
    referencedByMessage.set(
      msg.id,
      new Set(extractInlineImageFilenames(msg.bodyCleaned))
    );
  }

  for (const [messageId, filenames] of referencedByMessage) {
    for (const filename of filenames) {
      const att = ingestedByFilename.get(filename);
      if (!att || att.emailMessageId === messageId) continue;

      // Surface the chip on this referencing message.
      const bucket = map.get(messageId) ?? [];
      if (!bucket.some((a) => a.id === att.id)) {
        bucket.push(att);
        map.set(messageId, bucket);
      }

      // Drop it from the dedup-host (the forwarded cover) unless that host's own
      // body also references the file — otherwise the chip there is a pure
      // forward-cover duplicate of what now renders inline on this message.
      const hostReferences =
        referencedByMessage.get(att.emailMessageId)?.has(filename) ?? false;
      if (!hostReferences) {
        removeAttachmentFromBucket(map, att.emailMessageId, att.id);
      }
    }
  }

  return map;
}

// ---------------------------------------------------------------------------
// Retroactive note created-date helpers
//
// A note can be backdated at creation so it lands in the right timeline slot.
// The picker works in whole local days (YYYY-MM-DD); these helpers convert to
// the ISO datetime the API expects and format the date chip. Shared by both
// note-composer surfaces (inline activity feed + quick-create dialog) so the
// conversion stays identical.
// ---------------------------------------------------------------------------

/** Today as YYYY-MM-DD in the user's local timezone (DatePicker `maxDate`). */
export function noteDateToday(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert a YYYY-MM-DD picker value into an ISO datetime anchored at local noon
 * for that day. Noon avoids a tz-boundary day-shift when the value crosses
 * midnight UTC.
 */
export function noteDateToIso(date: string): string {
  return new Date(`${date}T12:00:00`).toISOString();
}

/** Format a YYYY-MM-DD picker value for the date chip, e.g. "Mar 12, 2024". */
export function formatNoteDateChip(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
